import {
  ArrowRightTwoIcon,
  CloseIcon,
  DeleteIcon,
  LeftArrowIcon,
  ReloadIcon,
} from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import cn from 'classnames';
import format from 'date-fns/format';
import { on } from 'events';
import castArray from 'lodash/castArray';
import * as React from 'react';
import type { CalendarProps as ReactCalendarProps } from 'react-calendar';
import ReactCalendar, { OnChangeDateRangeCallback } from 'react-calendar';

import { Button } from '../button';
import { Switch } from '../switch';
import styles from './calendar.module.css';
import { EditRepeat } from './components/EditRepeat';
import { SelectReminder } from './components/SelectReminder';
import { TimePicker } from './TimePicker';
import { CalendarRepeat, OnChangeRepeatCallback } from './types/calendar';

const { useState, useRef, useEffect } = React;
export type CalendarTabs = 'date' | 'startEndDate';

export interface RecurringProps {
  recurringData?: CalendarRepeat;
  onChangeRepeat?: OnChangeRepeatCallback;
  customPeriodicElements?: any;
}
export interface ReminderProps extends RecurringProps {
  onChangeReminder?: () => void;
  reminderOffset?: string | number;
}
export interface CalendarProps extends ReminderProps {
  value: [Date] | [Date, Date] | Date[] | null;
  onChange: OnChangeDateRangeCallback;
  onDone?: () => void;
  configs?: ReactCalendarProps;
  // not removing below to maintain compatibility with other components for now
  initialTab?: CalendarTabs;
  show?: CalendarTabs[];
  disableEndDate?: boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
  showReminder?: boolean;
  showRecurring?: boolean;
  isScrum?: boolean;
  endDateFirst?: boolean;
}

export const Calendar = ({
  value = null,
  onChange,
  onChangeReminder,
  onChangeRepeat,
  onDone,
  configs,
  disableEndDate = false,
  showReminder,
  showRecurring,
  minDate,
  maxDate,
  reminderOffset,
  recurringData,
  isScrum,
  endDateFirst = false,
}: CalendarProps) => {
  const [recurringTaskValue, setRecurringTaskValue] = useState<CalendarRepeat>(
    recurringData?.type ? recurringData : {}
  );
  const [startDate, setStartDate] = useState<Date | null | undefined>(value?.[0]);
  const [endDate, setEndDate] = useState<Date | null | undefined>(value?.[1]);
  const [isEndDate, setIsEndDate] = useState(value?.[1] ? true : false);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(true);

  const startDateRef = useRef<HTMLInputElement | null>(null);
  const endDateRef = useRef<HTMLInputElement | null>(null);
  const calendarRef = useRef<HTMLInputElement | null>(null);

  const dateLabels = ['Start date', 'End date'];

  useEffect(() => {
    if (endDate && endDateRef.current) {
      endDateRef.current.value = endDate?.toDateString() ?? '';
    }
    if (startDate && startDateRef?.current) {
      startDateRef.current.value = startDate?.toDateString() ?? '';
    }
  }, [startDate, endDate, endDateRef.current, startDateRef.current]);

  useEffect(() => {
    if (!value || value.length === 0) {
      setStartDate(null);
      setEndDate(null);
    } else if (value.length === 1) {
      if (endDateFirst) {
        setEndDate(value[0]);
        setStartDate(null);
      } else {
        setStartDate(value[0]);
        setEndDate(null);
      }
    } else if (value.length === 2) {
      setEndDate(value[1]);
      setStartDate(value[0]);
    }
  }, [value]);

  const onDeleteStartDate = (e: any) => {
    if (startDateRef.current !== undefined && startDateRef?.current !== null) {
      setStartDate(null);
      if (endDateFirst) {
        setIsEndDate(false);
        onChange([endDate].filter(Boolean) as any, e);
        if (startDateRef.current) startDateRef.current.value = '';
      } else {
        onChange([] as any, e);
      }
    }
  };
  const onDeleteEndDate = (e: any) => {
    if (endDateRef.current !== undefined && endDateRef.current !== null) {
      endDateRef.current.value = '';
      setEndDate(null);
      if (endDateFirst) {
        setEndDate(null);
        setStartDate(null);
        onChange([] as any, e);
        if (startDateRef.current) startDateRef.current.value = '';
      } else {
        setIsEndDate(false);
        onChange([startDate].filter(Boolean) as any, e);
      }
    }
  };

  const onChangeRepeatTask = (calendarRepeat: CalendarRepeat) => {
    setRecurringTaskValue(calendarRepeat);
    onChangeRepeat?.(calendarRepeat);
  };

  const handleChange = (
    value: Date & ([Date] | [Date, Date]),
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const arrayDates = castArray(value);
    const dates: Date[] = [];

    const startDate = endDateFirst && arrayDates.length === 1 ? arrayDates[1] : arrayDates[0];
    const endDate = endDateFirst && arrayDates.length === 1 ? arrayDates[0] : arrayDates[1];

    if (startDate) {
      const startTime = format(startDate, 'p');
      const updatedDateTime = new Date(`${startDate.toDateString()} ${startTime}`);
      dates.push(updatedDateTime);
    }
    if (endDate) {
      const endTime = format(endDate, 'p');
      const updatedDateTime = new Date(`${endDate.toDateString()} ${endTime}`);
      dates.push(updatedDateTime);
    }

    if (onChange) {
      if (Array.isArray(value)) {
        onChange(dates as [Date] | [Date, Date], e);
      } else {
        onChange([value] as [Date], e);
      }
    }
    if (endDate) {
      setEndDate(endDate);
      if (endDateRef.current) endDateRef.current.value = endDate.toDateString();
    } else {
      setEndDate(null);
      if (endDateRef.current) endDateRef.current.value = '';
    }
    if (startDate) {
      setStartDate(startDate);
      if (startDateRef.current) startDateRef.current.value = startDate.toDateString();
    } else {
      setStartDate(null);
      if (startDateRef.current) startDateRef.current.value = '';
    }
  };

  const onStartTimeChange = (dateTime: Date) => {
    onChange([dateTime, endDate].filter(Boolean) as any, null as any);
  };

  const onEndTimeChange = (dateTime: Date) => {
    onChange([startDate, dateTime].filter(Boolean) as any, null as any);
  };

  const getDisableData = () => {
    if (!startDate && !endDate) {
      return 'date';
    } else if (recurringData?.type) {
      return 'recurring';
    } else return 'none';
  };

  return (
    <div className={styles['calendar-wrapper']}>
      {showDatePicker ? (
        <section>
          <section>
            {endDateFirst && isEndDate && (
              <div
                className={cn(
                  `w-224 h-48 bg-gray-100 rounded-lg border mb-12 mx-16 mt-16 flex items-center border-gray-200 focus:border-blue-300`
                )}
              >
                <input
                  placeholder={dateLabels[0]}
                  className="h-46 bg-gray-100 ml-8 placeholder-gray-500 font-regular text-sm text-left flex w-118"
                  type="text"
                  ref={startDateRef}
                  readOnly
                />
                {startDate && (
                  <TimePicker
                    inputDate={startDate}
                    setInputDate={setStartDate}
                    onTimeChange={onStartTimeChange}
                  />
                )}
                {startDate && (
                  <button
                    className="mt-4 mr-8 text-gray-500 float-right"
                    onClick={onDeleteStartDate}
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
            )}

            {(isEndDate || endDateFirst) && (
              <div
                className={cn(
                  `w-224 h-48 bg-gray-100 rounded-lg border mb-12 mx-16 mt-16 flex items-center border-gray-200 focus:border-blue-300`
                )}
              >
                <input
                  placeholder={dateLabels[1]}
                  className="bg-gray-100 ml-8 h-46 placeholder-gray-500 font-regular text-sm text-left flex w-118"
                  type="text"
                  ref={endDateRef}
                  readOnly
                />
                {endDate && (
                  <TimePicker
                    inputDate={endDate}
                    setInputDate={setEndDate}
                    onTimeChange={onEndTimeChange}
                  />
                )}
                {endDate && (
                  <button className="mt-4 mr-8 text-gray-500 float-right" onClick={onDeleteEndDate}>
                    <CloseIcon />
                  </button>
                )}
              </div>
            )}
          </section>
          <section className={styles['calendar-body']}>
            <ReactCalendar
              className={styles.calendar}
              onChange={handleChange}
              allowPartialRange={true}
              value={startDate && endDate ? [startDate, endDate] : startDate || endDate || null}
              returnValue={isEndDate ? 'range' : 'start'}
              selectRange={isEndDate}
              nextLabel={<ArrowRightTwoIcon />}
              prevLabel={<ArrowRightTwoIcon />}
              prev2Label={null}
              next2Label={null}
              formatMonth={formatMonth}
              formatShortWeekday={formatDay}
              minDetail={'decade'}
              maxDate={maxDate ? new Date(maxDate) : new Date(2049, 12, 31)}
              minDate={minDate ? new Date(minDate) : new Date(2001, 1, 1)}
              inputRef={calendarRef}
              {...configs}
            />
          </section>
          {isScrum || (
            <>
              {!disableEndDate && (
                <section>
                  <div className="flex flex-row h-40 border-b border-color px-16 justify-between items-center py-16">
                    <div
                      className={classNames(
                        isEndDate ? 'text-gray-999' : 'text-gray-500',
                        ' font-medium text-sm'
                      )}
                    >
                      {dateLabels[endDateFirst ? 0 : 1]}{' '}
                    </div>
                    <div className="">
                      <Switch
                        className="mt-2"
                        checked={isEndDate}
                        onChange={() => {
                          setIsEndDate(!isEndDate);
                          if (endDateFirst) {
                            setStartDate(null);
                            onChange([endDate].filter(Boolean) as any, null as any);
                          } else {
                            setEndDate(null);
                            onChange([startDate].filter(Boolean) as any, null as any);
                          }
                        }}
                      />
                    </div>
                  </div>
                </section>
              )}
            </>
          )}

          {/* REMINDER */}
          {showReminder && (
            <SelectReminder
              reminderOffset={reminderOffset}
              onChange={onChangeReminder}
              disable={getDisableData()}
            />
          )}
          <div
            className={classNames(
              showRecurring ? 'justify-between' : 'justify-end',
              'w-full flex flex-row  items-center font-medium text-base px-18 pt-8 pb-14'
            )}
          >
            {showRecurring ? (
              recurringTaskValue && recurringTaskValue.type ? (
                <div className="flex">
                  <button
                    className="float-right flex flex-row align-middle w-full h-full gap-4"
                    onClick={() => setShowDatePicker(false)}
                  >
                    <div className=" mt-3">
                      <ReloadIcon className="text-gray-500" />
                    </div>
                    <div className="text-gray-500 ">Repeat: On</div>
                  </button>
                  <Tippy content="Delete recurring task" className="w-auto h-auto bg-gray-999">
                    <button
                      onClick={() => {
                        onChangeRepeatTask?.({
                          disable: true,
                          recurringId: recurringTaskValue?.recurringId,
                        });
                      }}
                    >
                      <DeleteIcon className="text-xs ml-6 text-gray-500 -mt-14" />
                    </button>
                  </Tippy>
                </div>
              ) : (
                <button
                  className="float-right flex flex-row align-middle w-full h-full gap-4"
                  onClick={() => {
                    setShowDatePicker(false);
                    onChangeRepeatTask?.({ type: 'weekly', repeats: [0] });
                  }}
                >
                  <div className=" mt-3">
                    <ReloadIcon />
                  </div>
                  <div className="text-gray-999 ">Start repeat</div>
                </button>
              )
            ) : null}

            {onDone && (
              <Button status="primary" className=" self-end" onClick={onDone}>
                Done
              </Button>
            )}
          </div>
        </section>
      ) : (
        <section className="flex flex-col px-18 py-14">
          {/* REPEAT */}
          {showRecurring &&
            recurringTaskValue &&
            Object.keys(recurringTaskValue).length > 0 &&
            onChangeRepeat && (
              <div>
                <div className="mb-8">
                  <button onClick={() => setShowDatePicker(true)}>
                    <LeftArrowIcon wrapperClass="text-gray-500 text-xs mr-8" />
                  </button>
                  <label className={'text-sm text-gray-500 font-medium'}>Repeats</label>
                </div>
                <EditRepeat
                  recurringData={recurringTaskValue}
                  onChangeRepeat={onChangeRepeatTask}
                />
                <div className="flex justify-between my-auto items-center mt-20 mb-10">
                  <button
                    className="flex "
                    onClick={() => {
                      setShowDatePicker(true);
                      onChangeRepeatTask?.({ disable: true });
                    }}
                  >
                    <ReloadIcon className="text-gray-500 mr-4 mt-2 inline-block text-base" />
                    <div className="text-gray-500 font-medium text-sm">Stop repeat</div>
                  </button>
                  <Button status="primary" className=" self-end" onClick={onDone}>
                    Done
                  </Button>
                </div>
              </div>
            )}
        </section>
      )}
    </div>
  );
};
const formatMonth: ReactCalendarProps['formatMonth'] = (locale, date) => {
  return date.toLocaleString('default', { month: 'short' });
};

const formatDay: ReactCalendarProps['formatDay'] = (locale, date) => {
  return date.toDateString().slice(0, 2);
};

const initialRecurringData = { type: 'weekly', repeats: [0] };
